/* eslint-disable eqeqeq */
import { FC, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";

import { withProcedureWraper } from "../..";
import { Routes } from "../../../../AppRoutes";
import { Box } from "../../../../components/box";
import { Icon } from "../../../../components/icon";
import { InputSelect, InputText, InputTextArea } from "../../../../components/input";
import ModalNotification from "../../../../components/modal-notification";
import { CreateNotification } from "../../../../components/notifier";
import { Button } from "../../../../modules/button";
import { InputWraper, useForm } from "../../../../modules/form";
import { MailService } from "../../../../services/mail/mail.service";
import { useSelector } from "../../../../store/store";
import { codeProcessRequestTypes } from "../../info-technology-process/code-process/codeRequestType";

import { getEnv, isDev } from "../../../../AppConfigs";
import { mailRegexs } from "../../../../constants/mailTemplateConstants";
import { RequestStatus } from "../../../../constants/RequestStatus";
import { requestStatus } from "../../../../constants/statusConstants";

import { Col, Row } from "react-bootstrap";
import {
  getDDMMYYYFormat,
  getDay,
  getDiffDay,
  getHHmmFormat,
  getLocalDate,
  setUtcTime,
} from "../../../../helper/dateTimeHelper";
import { unitDN, unitNB, unitTSN } from "../../info-technology-process/data";
// import { VehicleContentForm, VehicleContentStructure } from "./form";
import { useQueryClient } from "@tanstack/react-query";
import { InputArrayDataV2 } from "../../../../components/input/arrayData/index-v2";
import { InputDateTimeAntd } from "../../../../components/input/date-time-antd";
import { InputSelectTag } from "../../../../components/input/select-tag";
import { eApproveTitleCode } from "../../../../constants/bizTitle";
import { DateHelper } from "../../../../helper";
import { useUser } from "../../../../hook";
import { BusinessTicketRequestService } from "../../../../services/company-office/BusinessTicketService";
import { validateRequests } from "../../../../services/util";
import { ApproveCreatorItem } from "../../../../services/vos/business-tickets/approve-creator.service";
import { useSearchParamsV6 } from "../../../chats/hooks/useSearchParamsV6";
import { Upload } from "../../upload";
import { ticketTypeRequest } from "../companyRequestContants/companyRequestContants";
import ApprovalProcess, { ApprovalProcessApprover, ApprovalProcessHistory } from "./components/approval-process";
import ReservationRequestTable from "./components/reservation-request-table";
import SelectDepartmentTree from "./components/select-department-tree";
import { BusinessTicketContextProvider } from "./contexts/business-ticket-context";
import { BusinessTicketContentFormV2, BusinessTicketContentStructure } from "./formBusinessTicket";
import { useBusinessTicketsRequests } from "./hooks";
import useBusinessTicketApprovers from "./hooks/use-business-ticket-approvers";
import useHrProfile from "./hooks/use-hr-profile";

type StepHistoryPayload = {
  label: string;
  status: RequestStatus;
  updatedDateTime: string;
  reason: string;
};

type StepPayload = {
  title: string;
  name: string;
  email: string;
  isRequired: boolean;
  roles: string[];
  isApproved: boolean;
  isRejected: boolean;
  histories: StepHistoryPayload[];
};

interface BuildStepsOptions {
  derivedSteps: ApproveCreatorItem[];
  requester: { name: string; email: string; title: string };
  hasInitStep: boolean;
}

const getBeforeTicketTitle = (beforeRequest: any) => {
  const infos = [`Yêu cấp cấp vé công tác phiếu số ${beforeRequest.id}`];

  if (beforeRequest.fields.DepartmentScope) infos.push(beforeRequest.fields.DepartmentScope.split("|").at(-1));

  if (beforeRequest.fields.eWorkingTime && beforeRequest.fields.eFinishTime) {
    infos.push(DateHelper.formatDateToString(beforeRequest.fields.eWorkingTime));
    infos.push(DateHelper.formatDateToString(beforeRequest.fields.eFinishTime));
  }

  return infos.join(" - ");
};

const buildInitialStep = (requester: { name: string; email: string; title: string }): StepPayload => ({
  title: "Khởi tạo",
  name: requester.name,
  email: requester.email,
  isRequired: true,
  roles: [requester.title],
  isApproved: true,
  isRejected: false,
  histories: [
    {
      label: "Ngày tạo",
      status: RequestStatus.created,
      updatedDateTime: new Date().toISOString(),
      reason: "",
    },
  ],
});

const buildStepsPayload = ({ derivedSteps, requester, hasInitStep }: BuildStepsOptions): StepPayload[][] => {
  const approvalSteps = (derivedSteps ?? []).slice(hasInitStep ? 1 : 0).map((step) =>
    (step.Approvers ?? []).map((approver) => ({
      title: step.Title,
      name: approver.FullName ?? "",
      email: approver.Email ?? "",
      isRequired: approver.DutyType === "MAIN",
      roles: [approver.Name_Job_Title],
      isApproved: false,
      isRejected: false,
      histories: [],
      isSub: approver.DutyType === "SUB",
    })),
  );

  return [[buildInitialStep(requester)], ...approvalSteps];
};

/**
 * Convert ApproveCreatorItem[] to ApprovalProcessApprover[][]
 * @param steps - Array of ApproveCreatorItem
 * @param requester - Optional requester info for init step
 * @returns Array of arrays of ApprovalProcessApprover
 */
const convertApproveCreatorItemToApprovalProcess = (
  steps: ApproveCreatorItem[],
  requester?: { name: string; email: string },
): ApprovalProcessApprover[][] => {
  if (!steps?.length) return [];

  return steps.map((step) =>
    (step.Approvers ?? []).map((approver) => {
      // Check if this is init step (Level 0 and Title is "Khởi tạo")
      const histories: ApprovalProcessHistory[] = [];

      return {
        title: step.Title,
        name: approver.FullName ?? "",
        email: approver.Email ?? "",
        isRequired: approver.DutyType === "MAIN",
        roles: [approver.Name_Job_Title],
        isApproved: false,
        isRejected: false,
        histories,
        isSub: approver.DutyType === "SUB",
      };
    }),
  );
};

const siteUrl = getEnv("REACT_APP_APPROVER_BUSINESS_TICKET_REQUEST_URL");

export const createBusinessTicket: FC = withProcedureWraper(() => {
  const userData = useUser();

  const { pathname } = useLocation();
  const isDuplicate = pathname.includes("duplicate");

  const [searchParams] = useSearchParamsV6();
  const returnTicketId = searchParams.get("return-ticket");

  const history = useHistory();
  const params = useRouteMatch().params as any;
  const id = +params.id;
  const employees = useSelector((state: any) => state.employee.employees);
  const softwares = useSelector((state: any) => state.systemMgmt.softwares);
  const mailtemplates = useSelector((state: any) => state.systemMgmt.mailtemplates);
  // set State input field
  const [selectedDepartment, setSelectedDepartment] = useState(null as any);
  const [refundDepartment, setRefundDepartment] = useState(null as any);
  const [selectedUnit, setSelectedUnit] = useState(null as any);
  const [setUnit] = useState<any>([]);

  const today = new Date();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [, setApproverOptions] = useState([]);
  const [, setApprover] = useState("");
  const [requestType, setRequestType] = useState(codeProcessRequestTypes[0].value);
  const [ticketType, setTicketType] = useState(ticketTypeRequest[0].value as any);
  const [beforeRequest, setBeforeRequest] = useState(null as any);
  const [beforeRequestSelected, setBeforeRequestSelected] = useState(null as any);
  const { businessTicketsMyRequests: rawBusinessTicketsMyRequests } = useBusinessTicketsRequests();
  const queryClient = useQueryClient();

  // State để lưu trữ businessTicketsMyRequests ổn định
  const [stableBusinessTicketsMyRequests, setStableBusinessTicketsMyRequests] = useState<any[]>([]);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState<string>("");
  const [notificationTitle, setNotificationTitle] = useState<string>("Thông báo");

  // Chỉ update khi thực sự có thay đổi về length hoặc ids
  useEffect(() => {
    if (!rawBusinessTicketsMyRequests) return;

    const currentIds = rawBusinessTicketsMyRequests
      .map((item) => item.id)
      .sort()
      .join(",");
    const stableIds = stableBusinessTicketsMyRequests
      .map((item) => item.id)
      .sort()
      .join(",");

    if (rawBusinessTicketsMyRequests.length !== stableBusinessTicketsMyRequests.length || currentIds !== stableIds) {
      setStableBusinessTicketsMyRequests(rawBusinessTicketsMyRequests);
    }
  }, [rawBusinessTicketsMyRequests, stableBusinessTicketsMyRequests]);

  const [contentData, setContentData] = useState<any>([]);
  const [stepsPayload, setStepsPayload] = useState<StepPayload[][]>([]);
  const [reservationRequest, setReservationRequest] = useState(null as any);
  const [reservationRequestProcessed, setReservationRequestProcessed] = useState(null as any);

  const isRefund = ticketType === "refund";

  const { data: hrProfile, isLoading: isLoadingHrProfile } = useHrProfile({
    email: userData?.email,
  });

  const Dep_ID = hrProfile?.Dep_ID && hrProfile?.DepName_VN ? `${hrProfile.Dep_ID}|${hrProfile.DepName_VN}` : undefined;

  const {
    data: businessTicketApprovers,
    isLoading: isLoadingBusinessTicketApprovers,
    isFetching: isFetchingBusinessTicketApprovers,
  } = useBusinessTicketApprovers({
    depId: isRefund ? refundDepartment?.split("|")[0] : selectedDepartment?.split("|")[0],
    recUserId: hrProfile?.HR_ID?.toString(),
  });

  const canCreate = useMemo(
    () => businessTicketApprovers?.Data.every((item) => item.Approvers.every((i) => i.Email)),
    [businessTicketApprovers?.Data],
  );

  const { getInputProps, handleSubmit, isSubmitting, setValues } = useForm({
    structure: [
      {
        name: "type",
        label: "Nhu cầu",
      },
      {
        name: "beforeRequest",
        label: "Yêu cầu đã cấp trước đó",
      },
      {
        name: "department",
        label: "Phạm vi cấp cho cơ quan/ đơn vị",
      },
      {
        name: "unit",
        label: "Đơn vị",
      },
      {
        name: "phoneNumber",
        label: "SĐT liên hệ",
      },
      {
        name: "startDate",
        label: "Thời gian bắt đầu",
      },
      {
        name: "endDate",
        label: "Thời gian kết thúc",
      },
      {
        name: "tripItineraty",
        label: "Hành trình",
      },
      {
        name: "description",
        label: "Nội dung",
      },
      {
        name: "requests",
        label: "Yêu cầu đặt chỗ",
        defaultValue: [],
      },
      {
        name: "fileRequests",
        label: "Tệp đính kèm",
        defaultValue: [],
      },
      {
        name: "destinationBusiness",
        label: "Nơi đến công tác",
      },
      {
        name: "baseOnDecision",
        label: "Căn cứ theo quyết định số",
      },
      {
        name: "baseOnProposal",
        label: "Căn cứ theo tờ trình",
      },
      {
        name: "pickApprover",
        label: "Người duyệt",
      },
      {
        name: "findApprover",
        label: "Người duyệt thay thế",
      },
      {
        name: "pickItLead",
        label: "Bộ phận hành chính VPCT",
      },
    ],
    onSubmit: async (payload, _, __, additionalValue: any) => {
      const {
        // requests,
        fileRequests,
        destinationBusiness,
        tripItineraty,
        description,
        baseOnDecision,
        baseOnProposal,
      } = payload;
      const software: any = softwares.find((f: any) => f.eSystemCode === "TKR");

      // if (!selectedDepartment || !selectedUnit) {
      //   CreateNotification({ title: "Thông báo", body: "Vui lòng chọn CQ/ĐV!", icon: "/icons/warning.png" });
      //   return;
      // }

      if (contentData.length === 0 && id === 0) {
        setNotificationTitle("Thông báo");
        setNotificationMessage("Vui lòng nhập đầy đủ thông tin yêu cầu!");
        setIsNotificationModalOpen(true);
        return;
      }

      /**
       * Validate data of requests is valid ??
       */
      if (contentData.length) {
        const isValidateAllRequests = validateRequests(contentData);

        if (!isValidateAllRequests) {
          setNotificationTitle("Thông báo");
          setNotificationMessage("Yêu cầu đặt chỗ chứa dữ liệu không hợp lệ!");
          setIsNotificationModalOpen(true);
          return;
        }
      }

      // if (contentData.length > 0) {
      //   if (ticketType === "refund") {
      //     // let count = 0;
      //     // contentData.forEach((e: any) => {
      //     //   if (!e.TickNumber) count++;
      //     // });
      //     // if (count > 0) {
      //     //   CreateNotification({ title: "Thông báo", body: "Vui lòng Bổ sung số vé!", icon: "/icons/warning.png" });
      //     //   return;
      //     // }
      //   }
      // }

      // if (!approver && id === 0) {
      //   CreateNotification({ title: "Thông báo", body: "Vui lòng chọn thủ trưởng CQ/ĐV!", icon: "/icons/warning.png" });
      //   return;
      // }

      if (!startDate || !endDate) {
        setNotificationTitle("Thông báo");
        setNotificationMessage("Vui lòng nhập đầy đủ thông tin yêu cầu");
        setIsNotificationModalOpen(true);
        return;
      }

      if ((!destinationBusiness || !description) && ticketType !== "passport") {
        setNotificationTitle("Thông báo");
        setNotificationMessage("Vui lòng nhập đầy đủ thông tin yêu cầu");
        setIsNotificationModalOpen(true);
        return;
      }

      if ((!description || !tripItineraty) && ticketType !== "refund") {
        setNotificationTitle("Thông báo");
        setNotificationMessage("Vui lòng nhập đầy đủ thông tin yêu cầu");
        setIsNotificationModalOpen(true);
        return;
      }

      if (isRefund) {
        if (!reservationRequestProcessed) {
          setNotificationTitle("Thông báo");
          setNotificationMessage("Vui lòng nhập đầy đủ thông tin yêu cầu");
          setIsNotificationModalOpen(true);
          return;
        }

        if (!refundDepartment) {
          setNotificationTitle("Thông báo");
          setNotificationMessage("Vui lòng chọn cơ quan/đơn vị");
          setIsNotificationModalOpen(true);
          return;
        }
      }

      if (!tripItineraty || tripItineraty.length < 6) {
        setNotificationTitle("Thông báo");
        setNotificationMessage("Hành trình phải có ít nhất 6 ký tự!");
        setIsNotificationModalOpen(true);
        return;
      }

      if (id === 0) {
        if (!derivedSteps.length) {
          setNotificationTitle("Thông báo");
          setNotificationMessage("Không có thông tin quy trình duyệt! Vui lòng kiểm tra lại.");
          setIsNotificationModalOpen(true);
          return;
        }
      }

      const eDepartment = hrProfile?.OrgLevels.at(1)?.Name_VN || "";
      const eUnit = hrProfile?.OrgLevels.at(2)?.Name_VN || "";
      const requesterName = hrProfile?.UserName || userData?.name || "";
      const requesterEmail = hrProfile?.Email || userData?.email || "";
      const stepsForSubmission =
        derivedSteps.length > 0
          ? buildStepsPayload({
              derivedSteps,
              requester: { name: requesterName, email: requesterEmail, title: hrProfile?.JobTitle_NameVN || "" },
              hasInitStep: true,
            })
          : stepsPayload;
      const serializedSteps = JSON.stringify(stepsForSubmission);

      const firstApprover = derivedSteps.at(initStep ? 1 : 0)?.Approvers?.find((item) => item.DutyType === "MAIN");
      const secondApprover = derivedSteps.at(initStep ? 2 : 1)?.Approvers?.find((item) => item.DutyType === "MAIN");

      const assignees = software.eITStaff;
      const officialVehicleRequest: any =
        id === 0 || isDuplicate
          ? {
              fields: {
                Title: `Cấp vé công tác`,
                eApprovedBy: `${firstApprover?.Email}|${firstApprover?.Name_Job_Title}`,
                eApprovedByITLead: `${secondApprover?.Email}|${secondApprover?.Name_Job_Title}`,
                eApprovedByITHead: `${software.eITHead}|${software.eITHeadName}`,
                eAssignee: assignees,
                eOffice: destinationBusiness,
                eWorkingTime: setUtcTime(getDay(startDate)),
                eFinishTime: setUtcTime(getDay(endDate)),
                eBaseOnDecision: baseOnDecision,
                eBaseOnProposal: baseOnProposal?.length > 0 ? JSON.stringify(baseOnProposal) : null,
                eTrip: tripItineraty,
                eTicketType: ticketType,
                eDescription: description,
                eRequestStatus: additionalValue?.requestStatus || requestStatus.submitted,
                eRequestType: requestType,
                eContent: JSON.stringify(contentData),
                eFileContent: JSON.stringify(fileRequests),
                // ePhoneNumber: formattedValue,
                ePhoneNumber: hrProfile?.Phone || "",
                eDepartment,
                eUnit,
                DepartmentScope: String(selectedDepartment),
                eSteps: serializedSteps,
              },
            }
          : {
              fields: {
                Title: `Cấp vé công tác`,
                // eApprovedBy: approverTmp? `${approverTmp.eMail}|${approverTmp.Title}`: `${approver}|${pickedUserName}`,
                // eApprovedByITLead: `${software.eITTeamLead}|${software.eTeamLeadName}`,
                // eApprovedByITHead: `${software.eITHead}|${software.eITHeadName}`,
                // eAssignee: assignees,
                eOffice: destinationBusiness,
                eWorkingTime: setUtcTime(getDay(startDate)),
                eFinishTime: setUtcTime(getDay(endDate)),
                eBaseOnDecision: baseOnDecision,
                eBaseOnProposal: baseOnProposal?.length > 0 ? JSON.stringify(baseOnProposal) : null,
                eTrip: tripItineraty,
                eTicketType: ticketType,
                eDescription: description,
                eRequestStatus: additionalValue?.requestStatus || requestStatus.submitted,
                // eRequestType: requestType,
                eContent: JSON.stringify(contentData),
                eFileContent: JSON.stringify(fileRequests),
                ePhoneNumber: hrProfile?.Phone || "",
                DepartmentScope: selectedDepartment,
                eSteps: serializedSteps,
              },
            };

      if (isRefund) {
        addVehicleRequest({
          fields: {
            ...officialVehicleRequest.fields,
            DepartmentScope: refundDepartment,
            eContent: JSON.stringify(reservationRequestProcessed),
            refundFor: beforeRequest,
          },
        });
      } else addVehicleRequest(officialVehicleRequest);
    },
  });

  const handleSaveDraft = async () => {
    handleSubmit(undefined, {
      requestStatus: requestStatus.draft,
    });
  };

  const initStep: ApproveCreatorItem | undefined = useMemo<ApproveCreatorItem | undefined>(() => {
    if (!hrProfile) return;

    return {
      Level: 0,
      Title: "Khởi tạo",
      Approvers: [
        {
          DutyType: "MAIN",
          HR_No: hrProfile?.HR_ID?.toString() || "",
          FullName: hrProfile?.UserName || "",
          Email: hrProfile?.Email || "",
          Name_Job_Title: hrProfile?.JobTitle_NameVN || "",
        },
      ],
    };
  }, [hrProfile]);

  const derivedSteps = useMemo<ApproveCreatorItem[]>(() => {
    if (!businessTicketApprovers?.Data?.length) return [];

    if (initStep) return [initStep, ...businessTicketApprovers?.Data];

    return businessTicketApprovers?.Data;
  }, [businessTicketApprovers?.Data, initStep]);

  const convertedSteps = useMemo<ApprovalProcessApprover[][]>(() => {
    if (!derivedSteps?.length) return [];

    const requester = hrProfile
      ? {
          name: hrProfile.UserName || userData?.name || "",
          email: hrProfile.Email || userData?.email || "",
        }
      : undefined;

    return convertApproveCreatorItemToApprovalProcess(derivedSteps, requester);
  }, [derivedSteps, hrProfile, userData]);

  const sendMail = async (approver: any, vehicleRequest: any) => {
    const url = siteUrl + "/details/" + vehicleRequest.id;
    const arr = approver.split("|");
    const tomail = arr.length > 0 ? arr[0] : "";
    const name = arr.length > 1 ? arr[1] : "";
    const template = mailtemplates.find((m: any) => m.eMailType === "BTRA");
    const content = template.eContent
      .replace(mailRegexs.zero + ";", name)
      .replace(mailRegexs.one + ";", "duyệt")
      .replace(mailRegexs.two + ";", vehicleRequest.createdBy.user.displayName)
      .replace(
        mailRegexs.three + ";",
        `${getDDMMYYYFormat(vehicleRequest.createdDateTime)} - ${getHHmmFormat(vehicleRequest.createdDateTime)}`,
      )
      .replace(mailRegexs.siteUrl, url);

    const subject = template.eSubject;
    const message = MailService.buildMailMesssage(subject, content, "html", tomail);
    await MailService.sendMail(message);
  };

  const addVehicleRequest = (officialVehicleRequest: any) => {
    if (id != 0 && !isDuplicate) {
      BusinessTicketRequestService.updateBusinessTicketRequest(id, officialVehicleRequest.fields).then(
        (res) => {
          CreateNotification({
            title: "Thành công",
            body: `Chỉnh sửa yêu cầu thành công.`,
            icon: "/icons/success.png",
          });
          queryClient.invalidateQueries({ queryKey: ["business-tickets-requests"] });
          history.push(Routes.myProcedureCollaborateTicketDetailPage.path.replace(":id", id));
        },
        (error) => {
          CreateNotification({
            title: "Thất bại",
            body: `Chỉnh sửa yêu cầu thất bại.`,
            icon: "/icons/error.png",
          });
          console.log(error);
        },
      );
    } else {
      BusinessTicketRequestService.addBusinessTicketRequest(officialVehicleRequest).then(
        (res) => {
          CreateNotification({
            title: "Thành công",
            body: `Tạo yêu cầu thành công.`,
            icon: "/icons/success.png",
          });
          queryClient.invalidateQueries({ queryKey: ["business-tickets-requests"] });
          if (officialVehicleRequest.fields.eApprovedBy) {
            sendMail(officialVehicleRequest.fields.eApprovedBy, res);
          }
          history.replace(Routes.myProcedureCollaborateTicketDetailPage.path.replace(":id", res.id));
        },
        (error) => {
          CreateNotification({
            title: "Thất bại",
            body: `Tạo yêu cầu thất bại.`,
            icon: "/icons/error.png",
          });
          console.log(error);
        },
      );
    }
  };

  const getApproverOptions = () => {
    let approvers: any = [];
    if (selectedDepartment && selectedDepartment.id > 0 && selectedUnit) {
      approvers = employees
        .filter((f: any) => eApproveTitleCode.indexOf(f.eBizTitleCode) > -1 && f.eDepartmentCode === selectedUnit.id)
        .map((o: any) => ({
          label: o.Title,
          value: o.eMail,
          default: o.eDefault || false,
        }));

      if (selectedDepartment.id === 1) {
        const tsnHead = isDev
          ? [
              {
                label: "Phạm Phú Yên",
                value: "canbo1@viags.vn",
                default: true,
              },
            ]
          : [
              {
                label: "Phạm Phú Yên",
                value: "yenpp@viags.vn",
                default: true,
              },
              {
                label: "Nguyễn Vĩnh Hưng",
                value: "hungnv@viags.vn",
                default: true,
              },
            ];
        approvers = [...tsnHead];
      }
    } else if (selectedDepartment && selectedDepartment.id != 0)
      approvers = employees
        .filter(
          (f: any) => eApproveTitleCode.indexOf(f.eBizTitleCode) > -1 && f.eDepartmentCode === selectedDepartment.id,
        )
        .map((o: any) => ({
          label: o.Title,
          value: o.eMail,
          default: o.eDefault || false,
        }));
    setApproverOptions(approvers);
    // const defaultApprover = approvers.find((a: any) => a.default);
    // setApprover(defaultApprover.value || approvers[0].value);
  };

  const onTicketTypeChange = (selectedOptions: any) => {
    setTicketType(selectedOptions);
    setContentData([]);
    setBeforeRequestSelected(null);
    setRefundDepartment(null);
    setReservationRequestProcessed(null);
  };

  useEffect(() => {
    if (selectedDepartment) {
      if (selectedDepartment.id === 2) {
        setUnit(unitNB);
        setSelectedUnit(null);
      } else if (selectedDepartment.id === 3) {
        setUnit(unitDN);
        setSelectedUnit(null);
      } else if (selectedDepartment.id === 1) {
        setUnit(unitTSN);
        setSelectedUnit(null);
      } else {
        setSelectedUnit(selectedDepartment);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartment]);

  useEffect(() => {
    if (selectedDepartment) {
      let id = selectedDepartment.id;
      switch (id) {
        case id === 1:
          break;
      }
    }
  }, [selectedDepartment]);

  useEffect(() => {
    setContentData([]);
  }, [selectedDepartment]);

  const onStartDateChange = (date: any) => {
    const newDate = new Date(date);
    setStartDate(newDate);

    // Nếu ngày kết thúc hiện tại < ngày bắt đầu mới thì mới cập nhật lại ngày kết thúc
    if (endDate && endDate < newDate) {
      setEndDate(newDate);
    }
  };

  const onEndDateChange = (date: any) => {
    const newDate = new Date(date);
    setEndDate(newDate);
    const days = getDiffDay(getLocalDate(newDate), getLocalDate(getDay(endDate)));
    if (days < 0) {
      setEndDate(newDate);
    }
  };

  const handleBeforeRequestChange = (e: any) => {
    const {
      eDescription,
      eOffice,
      // eApprovedBy,
      // eSoftwareId,
      eRequestType,
      eContent,
      // eTicketType,
      eFileContent,
      ePhoneNumber,
      eTrip,
      eWorkingTime,
      eFinishTime,
      eBaseOnDecision,
      eBaseOnProposal,
      DepartmentScope,
    } = e.value;
    // const approverEmail = eApprovedBy.split("|")[0];
    setValues({
      description: eDescription,
      requests: JSON.parse(eContent),
      fileRequests: JSON.parse(eFileContent),
      phoneNumber: ePhoneNumber,
      destinationBusiness: eOffice,
      tripItineraty: eTrip,
      startDate: eWorkingTime,
      endDate: eFinishTime,
      baseOnDecision: eBaseOnDecision,
      baseOnProposal: eBaseOnProposal ? JSON.parse(eBaseOnProposal) : [],
      department: Number(DepartmentScope),
    });
    // setApprover(approverEmail);
    // setSoftwareSelected(eSoftwareId);
    setBeforeRequestSelected(e.value);
    setRequestType(eRequestType);
    setContentData(JSON.parse(eContent));
    setStartDate(new Date(eWorkingTime));
    setEndDate(new Date(eFinishTime));
    setBeforeRequest(e.id);
    setReservationRequest(JSON.parse(eContent));
    setRefundDepartment(e.value.DepartmentScope);
  };

  const handleChangeDepartment = (value: any) => {
    setSelectedDepartment(value);
  };

  useEffect(() => {
    if (id !== 0) {
      BusinessTicketRequestService.getBusinessTicketRequest(id)
        .then((res) => {
          const {
            eDescription,
            eOffice,
            eApprovedBy,
            // eSoftwareId,
            eRequestType,
            eContent,
            eTicketType,
            eFileContent,
            ePhoneNumber,
            eTrip,
            eWorkingTime,
            eFinishTime,
            eBaseOnDecision,
            eBaseOnProposal,
            DepartmentScope,
            eSteps,
          } = res.fields;
          const approverEmail = eApprovedBy.split("|")[0];

          const steps = JSON.parse(eSteps || "[]");
          setStepsPayload(Array.isArray(steps) ? steps : []);

          setValues({
            description: eDescription,
            requests: JSON.parse(eContent),
            fileRequests: JSON.parse(eFileContent),
            phoneNumber: ePhoneNumber,
            destinationBusiness: eOffice,
            tripItineraty: eTrip,
            startDate: eWorkingTime,
            endDate: eFinishTime,
            baseOnDecision: eBaseOnDecision,
            baseOnProposal: eBaseOnProposal ? JSON.parse(eBaseOnProposal) : [],
            department: Number(DepartmentScope),
            beforeRequest: res.fields.refundFor,
          });
          setApprover(approverEmail);
          // setSoftwareSelected(eSoftwareId);
          setContentData(JSON.parse(eContent));
          setRequestType(eRequestType);
          setStartDate(new Date(eWorkingTime));
          setEndDate(new Date(eFinishTime));
          setTicketType(eTicketType);
          setSelectedDepartment(DepartmentScope);

          if (eTicketType) {
            const beforeRequest = stableBusinessTicketsMyRequests.find(
              (e: any) => e.id === res.fields.refundFor.toString(),
            );
            if (beforeRequest) {
              setBeforeRequestSelected(beforeRequest.fields);
              setReservationRequest(JSON.parse(beforeRequest.fields.eContent));
            } else {
              setReservationRequest(JSON.parse(res.fields.eContent));
            }

            setReservationRequestProcessed(JSON.parse(res.fields.eContent));
            setRefundDepartment(res.fields.DepartmentScope);
            setBeforeRequest(res.fields.refundFor);
          }
        })
        .catch((error) => console.log(error));
    } else {
      setValues({
        department: Number(Dep_ID),
      });

      setSelectedDepartment(Dep_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, stableBusinessTicketsMyRequests]);

  useEffect(() => {
    getApproverOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartment, selectedUnit]);

  useEffect(() => {
    if (returnTicketId) {
      const beforeRequest = stableBusinessTicketsMyRequests.find((e: any) => e.id === returnTicketId);
      if (beforeRequest) {
        handleBeforeRequestChange({ value: beforeRequest.fields });
        setTicketType("refund");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnTicketId, stableBusinessTicketsMyRequests]);

  return (
    <BusinessTicketContextProvider
      value={{
        department: selectedDepartment,
      }}
    >
      <div className="ITCodeGrantedCreateNewPage">
        <div className="pd15">
          <div className="PageHead">
            <div className="title">
              <div className="btnBack" onClick={() => history.push(Routes.procedureCollaborateTicket.path)}>
                <Icon.ArrowLeft />
              </div>
              {id === 0 || isDuplicate ? "Tạo Đề Nghị Cấp/Hoàn Vé Công Tác" : "Chỉnh sửa Đề Nghị Cấp/Hoàn Vé Công Tác"}
            </div>
          </div>
          <Col>
            <form onSubmit={handleSubmit}>
              <Box title="Thông tin">
                <Row>
                  <Col sm="12" md="12">
                    <InputWraper
                      className="pb-3"
                      inputProps={getInputProps("type")}
                      renderInput={(s) => (
                        <InputSelectTag
                          {...s}
                          options={ticketTypeRequest}
                          value={ticketType}
                          onChange={onTicketTypeChange}
                        />
                      )}
                    />
                  </Col>
                </Row>
                {ticketType === "refund" && (
                  <Row>
                    <Col sm="12" md="12">
                      <InputWraper
                        className="pb-3"
                        inputProps={getInputProps("beforeRequest")}
                        isRequired
                        renderInput={(s) => (
                          <InputSelect
                            {...s}
                            options={stableBusinessTicketsMyRequests
                              .filter(
                                (e: any) =>
                                  (e.fields.eTicketType === "full" || e.fields.eTicketType === "ticket") &&
                                  e.fields.eRequestStatus === requestStatus.completed,
                              )
                              .map((o: any) => ({
                                id: o.id,
                                label: getBeforeTicketTitle(o),
                                value: { ...o.fields },
                              }))
                              .sort((a, b) => b.id - a.id)}
                            value={beforeRequestSelected}
                            onChange={handleBeforeRequestChange}
                            placeholder="* Chọn yêu cầu đã cấp trước đó"
                          />
                        )}
                      />
                    </Col>
                  </Row>
                )}
                {/* {id === 0 && (
                <Row>
                  <Col sm="6" md="6">
                    <InputWraper
                      inputProps={getInputProps("deparment")}
                      isRequired
                      renderInput={(s) => (
                        <InputSelect
                          {...s}
                          options={department.map((o: any) => ({
                            id: o.id,
                            label: o.value,
                            value: o.value,
                          }))}
                          value={selectedDepartment}
                          onChange={onDepartmentChange}
                          placeholder="* Chọn Cơ quan/ đơn vị"
                        />
                      )}
                    />
                  </Col>
                  <Col sm="6" md="6">
                    <InputWraper
                      inputProps={getInputProps("unit")}
                      isRequired
                      renderInput={(s) => (
                        <InputSelect
                          {...s}
                          options={unit.map((o: any) => ({
                            id: o.id,
                            label: o.value,
                            value: o.value,
                          }))}
                          value={selectedUnit?.value}
                          onChange={onUnitChange}
                          placeholder={"* Chọn đơn vị"}
                        />
                      )}
                    />
                  </Col>
                </Row>
              )}
              <InputWraper
                inputProps={getInputProps("phoneNumber")}
                renderInput={(s) => <InputNumber {...s} placeholder="Nhập SĐT liên hệ" />}
                isRequired
              /> */}

                {!isRefund && id == 0 && (
                  <Row>
                    <Col sm="12" md="6">
                      <InputWraper
                        inputClassName="border-none"
                        isRequired
                        className="pb-3"
                        inputProps={getInputProps("department")}
                        renderInput={(s) => (
                          <SelectDepartmentTree {...s} value={selectedDepartment} onChange={handleChangeDepartment} />
                        )}
                      />
                    </Col>
                  </Row>
                )}

                {!isRefund && (
                  <Row>
                    <Col sm="6" md="6">
                      <InputWraper
                        inputProps={getInputProps("startDate")}
                        renderInput={(s) => (
                          <>
                            <InputDateTimeAntd {...s} value={startDate} onChange={onStartDateChange} />
                          </>
                        )}
                      />
                    </Col>
                    <Col sm="6" md="6">
                      <InputWraper
                        inputProps={getInputProps("endDate")}
                        renderInput={(s) => (
                          <>
                            <InputDateTimeAntd {...s} value={endDate} onChange={onEndDateChange} minDate={startDate} />
                          </>
                        )}
                      />
                    </Col>
                  </Row>
                )}
                <Row>
                  {ticketType === "refund" || (
                    <Col sm="6" md="6">
                      <InputWraper
                        inputProps={getInputProps("baseOnDecision")}
                        renderInput={(s) => <InputText {...s} placeholder="Nhập Căn cứ theo quyết định số" />}
                      />
                    </Col>
                  )}
                  <Col sm={ticketType === "refund" ? 12 : 6} md={ticketType === "refund" ? 12 : 6}>
                    <InputWraper inputProps={getInputProps("baseOnProposal")} renderInput={(s) => <Upload {...s} />} />
                  </Col>
                </Row>
                {!isRefund && (
                  <Row>
                    <Col sm="6" md="6">
                      <InputWraper
                        inputProps={getInputProps("destinationBusiness")}
                        renderInput={(s) => <InputText {...s} placeholder="VD: Đà Nẵng, Nội Bài" />}
                        isRequired={ticketType !== "refund"}
                      />
                    </Col>
                    <Col sm="6" md="6">
                      <InputWraper
                        inputProps={getInputProps("tripItineraty")}
                        renderInput={(s) => <InputText {...s} placeholder="VD: SGN-HAN-SGN" />}
                        isRequired={ticketType !== "passport"}
                      />
                    </Col>
                  </Row>
                )}
                <InputWraper
                  inputProps={getInputProps("description", "Nhập nội dung")}
                  isRequired
                  component={InputTextArea}
                />
              </Box>

              <Box title="Yêu cầu đặt chỗ">
                {isRefund ? (
                  <ReservationRequestTable
                    reservationRequests={reservationRequest}
                    setProcessedData={setReservationRequestProcessed}
                    processedData={reservationRequestProcessed}
                  />
                ) : (
                  <InputWraper
                    isRequired
                    inputProps={getInputProps("requests")}
                    renderInput={(state) => (
                      <InputArrayDataV2
                        {...state}
                        isDisabled={!selectedDepartment}
                        // isKeepValue={true}
                        isTrip={true}
                        isOnlyPassport={ticketType === "passport"}
                        isRefund={ticketType === "refund"}
                        onChange={(e) => setContentData(e)}
                        value={contentData}
                        // isSelectUnit={selectedUnit ? 1 : 0}
                        form={BusinessTicketContentFormV2}
                        structure={BusinessTicketContentStructure}
                        excelTemplate="business-ticket"
                      />
                    )}
                  />
                )}

                <InputWraper inputProps={getInputProps("fileRequests")} renderInput={(s) => <Upload {...s} />} />
              </Box>

              {convertedSteps?.length > 0 && <ApprovalProcess loading={false} steps={convertedSteps} />}

              <div className="textRight">
                <Button
                  type="submit"
                  buttonType="success"
                  label="Lưu & gửi"
                  isLoading={isSubmitting}
                  disabled={
                    isLoadingHrProfile ||
                    isLoadingBusinessTicketApprovers ||
                    isFetchingBusinessTicketApprovers ||
                    !canCreate
                  }
                />
                <Button
                  type="button"
                  label="Lưu"
                  isLoading={isSubmitting}
                  disabled={
                    isLoadingHrProfile ||
                    isLoadingBusinessTicketApprovers ||
                    isFetchingBusinessTicketApprovers ||
                    !canCreate
                  }
                  className="ml10"
                  onClick={handleSaveDraft}
                />
                <Button
                  type="button"
                  label="Huỷ"
                  buttonType="grey-outline"
                  className="ml10"
                  onClick={() => history.push(Routes.procedureCollaborateTicket.path)}
                  disabled={isLoadingHrProfile || isSubmitting}
                />
              </div>
            </form>
          </Col>
        </div>
      </div>
      <ModalNotification
        open={isNotificationModalOpen}
        type="warning"
        title={notificationTitle}
        message={notificationMessage}
        onCancel={() => setIsNotificationModalOpen(false)}
        onOk={() => setIsNotificationModalOpen(false)}
      />
    </BusinessTicketContextProvider>
  );
});
